import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { FileText, Trash2, Sparkles, Copy, Printer, Upload, Download } from "lucide-react";

const KIND_LABEL = { pcn_letter: "PCN letter", evidence: "Evidence" };
const esc = (t) => t.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export const FineAppealDialog = ({ fine, editable, onClose, onSaved }) => {
  const [files, setFiles] = useState([]);
  const [text, setText] = useState("");
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const letterRef = useRef(null);
  const evidenceRef = useRef(null);

  const loadFiles = () => api.get(`/fines/${fine.id}/files`).then((r) => setFiles(r.data)).catch(() => {});

  useEffect(() => {
    if (fine) {
      setText(fine.appeal_text || "");
      api.get(`/fines/${fine.id}/files`).then((r) => setFiles(r.data)).catch(() => {});
    }
  }, [fine]);

  if (!fine) return null;

  const upload = async (kind, fileList) => {
    if (!fileList?.length) return;
    setUploading(true);
    try {
      for (const f of fileList) {
        const fd = new FormData();
        fd.append("kind", kind);
        fd.append("file", f);
        await api.post(`/fines/${fine.id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${fileList.length > 1 ? "Files" : "File"} attached`);
      loadFiles();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setUploading(false);
    }
  };

  const removeFile = async (id) => {
    await api.delete(`/fines/files/${id}`);
    loadFiles();
  };

  const generate = async () => {
    setGenerating(true);
    setText("");
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/fines/${fine.id}/appeal/generate`, {
        method: "POST",
        credentials: "include",
      });
      if (!resp.ok || !resp.body) throw new Error("Could not start AI generation");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = JSON.parse(line.slice(5));
          if (payload.delta) setText((t) => t + payload.delta);
          if (payload.reset) {
            setText("");
            if (payload.info) toast(payload.info);
          }
          if (payload.error) throw new Error(payload.error);
        }
      }
      onSaved?.();
      toast.success("Appeal drafted — review and edit before sending");
    } catch (e) {
      toast.error(e.message || "AI generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const saveText = async () => {
    setSaving(true);
    try {
      await api.put(`/fines/${fine.id}/appeal`, { appeal_text: text });
      toast.success("Appeal saved");
      onSaved?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Appeal copied to clipboard");
    } catch {
      toast.error("Could not copy — select the text manually");
    }
  };

  const printText = () => {
    const w = window.open("", "_blank");
    w.document.write(
      `<html><head><title>Appeal — ${esc(fine.reference)}</title><style>body{font-family:Georgia,serif;max-width:700px;margin:40px auto;line-height:1.65;white-space:pre-wrap;font-size:14px;color:#111}</style></head><body>${esc(text)}</body></html>`
    );
    w.document.close();
    w.focus();
    w.print();
  };

  const hasLetter = files.some((f) => f.kind === "pcn_letter");
  const btnCls = "rounded-none border-border h-9 text-[10px] uppercase tracking-widest font-bold bg-transparent text-foreground hover:bg-muted hover:text-foreground";

  return (
    <Dialog open={!!fine} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border rounded-none text-foreground sm:max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="appeal-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-black tracking-tighter text-2xl">
            APPEAL PCN <span className="text-[#FF3B30]">{fine.reference}</span>
          </DialogTitle>
          <div className="text-xs text-muted-foreground font-mono">{fine.vehicle_reg} · {fine.date} · £{Number(fine.amount).toFixed(2)}{fine.location ? ` · ${fine.location}` : ""}</div>
        </DialogHeader>

        <div className="space-y-5">
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">1 · PCN letter & evidence</div>
              {editable && (
                <div className="flex gap-2">
                  <input ref={letterRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={(e) => { upload("pcn_letter", Array.from(e.target.files)); e.target.value = ""; }} />
                  <input ref={evidenceRef} type="file" multiple accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={(e) => { upload("evidence", Array.from(e.target.files)); e.target.value = ""; }} />
                  <Button data-testid="upload-pcn-letter-button" variant="outline" disabled={uploading} onClick={() => letterRef.current?.click()} className={btnCls}>
                    <Upload size={13} className="mr-1" /> PCN letter
                  </Button>
                  <Button data-testid="upload-evidence-button" variant="outline" disabled={uploading} onClick={() => evidenceRef.current?.click()} className={btnCls}>
                    <Upload size={13} className="mr-1" /> Evidence
                  </Button>
                </div>
              )}
            </div>
            <div className="border border-border divide-y divide-border/60" data-testid="fine-files-list">
              {files.map((f) => (
                <div key={f.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                  <FileText size={14} className="text-muted-foreground shrink-0" />
                  <span className={`border px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-bold shrink-0 ${f.kind === "pcn_letter" ? "text-[#FF3B30] border-[#FF3B30]/40" : "text-muted-foreground border-border"}`}>{KIND_LABEL[f.kind] || f.kind}</span>
                  <span className="truncate flex-1">{f.filename}</span>
                  <a data-testid={`download-fine-file-${f.id}`} href={`${process.env.REACT_APP_BACKEND_URL}/api/fines/files/${f.id}/download`} target="_blank" rel="noreferrer" className="text-muted-foreground/70 hover:text-foreground"><Download size={14} /></a>
                  {editable && <button data-testid={`delete-fine-file-${f.id}`} onClick={() => removeFile(f.id)} className="text-muted-foreground/70 hover:text-[#FF3B30]"><Trash2 size={14} /></button>}
                </div>
              ))}
              {files.length === 0 && <div className="px-3 py-4 text-xs text-muted-foreground/70">No files yet. Upload the PCN letter (photo or PDF) — the AI reads it to write a stronger appeal.</div>}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">2 · Appeal letter</div>
              {editable && (
                <Button data-testid="generate-appeal-button" onClick={generate} disabled={generating} className="rounded-none bg-[#FF3B30] hover:bg-[#ff554b] font-bold uppercase tracking-widest text-[10px] h-9">
                  <Sparkles size={13} className="mr-1.5" /> {generating ? "Writing…" : text ? "Rewrite with AI" : "Write appeal with AI"}
                </Button>
              )}
            </div>
            {!hasLetter && !text && <div className="text-[11px] text-amber-600 dark:text-[#FFD60A] mb-2">Tip: attach the PCN letter first so the AI can pull the exact details from it.</div>}
            <Textarea
              data-testid="appeal-text-area"
              value={text}
              onChange={(e) => setText(e.target.value)}
              readOnly={!editable}
              placeholder="Your appeal letter will appear here — generate it with AI or write your own."
              className="rounded-none bg-background border-border min-h-[280px] font-mono text-[13px] leading-relaxed"
            />
          </div>

          <div className="flex gap-2 flex-wrap">
            {editable && (
              <Button data-testid="save-appeal-button" onClick={saveText} disabled={saving || generating || !text.trim()} className="rounded-none bg-foreground text-background hover:bg-foreground/85 font-bold uppercase tracking-widest text-[10px] h-10 px-5">
                {saving ? "Saving…" : "Save appeal"}
              </Button>
            )}
            <Button data-testid="copy-appeal-button" variant="outline" onClick={copyText} disabled={!text.trim()} className={`${btnCls} h-10 px-4`}>
              <Copy size={13} className="mr-1.5" /> Copy
            </Button>
            <Button data-testid="print-appeal-button" variant="outline" onClick={printText} disabled={!text.trim()} className={`${btnCls} h-10 px-4`}>
              <Printer size={13} className="mr-1.5" /> Print
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
